import { useMutation } from '@tanstack/react-query'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../services/firebase.service'

/**
 * Custom hook to call a Firebase function and manage the mutation.
 *
 * @template TInput - The type of the input data for the function.
 * @template TOutput - The type of the output data from the function.
 * @param {string} functionName - The name of the Firebase function to call.
 * @param {Function} [transformData] - Optional function to transform the input data before calling the function.
 * @returns {Mutation} - A mutation object from react-query that you can use to trigger the function.
 *
 * @example
 * const callFunction = useCallFirebaseFunction('createPaymentIntent');
 * callFunction.mutate({ amount, currency });
 * callFunction.mutate({ bookingId, formData })
 */
export const useCallFirebaseFunction = <TInput extends object, TOutput extends object>(
  functionName: string,
  transformData?: (data: TInput) => TInput | Promise<TInput>,
  options: { retry?: false } = {}
) => {
  return useMutation<TOutput, Error, TInput, unknown>({
    ...options,
    mutationFn: async (data: TInput) => {
      // Create a reference to the callable Firebase function
      const callableFunction = httpsCallable(functions, functionName)

      try {
        // Optionally transform the data before calling the function
        const transformedData = transformData ? await transformData(data) : data

        // Call the Firebase function
        const result = await callableFunction(transformedData)

        // Return the result's data assuming the response contains "data" property
        return result.data as TOutput // Ensure the return type matches the expected output
      } catch (error: any) {
        // Log error for debugging and return a custom error message if needed
        console.error('Error during Firebase function call', error)
        if (error) {
          throw error
        } else {
          throw new Error('Failed to execute Firebase function')
        }
      }
    },
  })
}
